import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  RefreshCw, Package, IndianRupee, Store, CheckCircle, XCircle, 
  AlertTriangle, Clock, Upload, Loader2, Wifi, WifiOff
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const SyncDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [syncData, setSyncData] = useState({ stores: [], total_stores: 0 });
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingStore, setSyncingStore] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', storeId: null });

  const fetchSyncStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/sync/store-sync-status');
      setSyncData(res.data || { stores: [], total_stores: 0 });
    } catch (error) {
      console.error('Error fetching sync status:', error);
      toast({ title: 'Error', description: 'Failed to fetch sync status', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const handleBulkSync = async (type) => {
    setSyncingAll(true);
    try {
      let endpoint = '/sync/bulk-product-sync';
      if (type === 'pricing') endpoint = '/sync/bulk-pricing-sync';
      
      const res = await api.post(endpoint);
      toast({ 
        title: 'Sync Complete', 
        description: res.data?.message || `${type === 'pricing' ? 'Pricing' : 'Products'} synced successfully`,
      });
      fetchSyncStatus();
    } catch (error) {
      console.error('Sync error:', error);
      toast({ title: 'Sync Failed', description: error.response?.data?.detail || 'Sync operation failed', variant: 'destructive' });
    } finally {
      setSyncingAll(false);
      setConfirmDialog({ open: false, type: '', storeId: null });
    }
  };

  const handleStoreSync = async (storeId, syncType) => {
    setSyncingStore(storeId);
    try {
      const res = await api.post(`/sync/sync-store/${storeId}?sync_type=${syncType}`);
      toast({ 
        title: 'Sync Complete', 
        description: res.data?.message || 'Store synced successfully',
      });
      fetchSyncStatus();
    } catch (error) {
      console.error('Store sync error:', error);
      toast({ title: 'Sync Failed', description: error.response?.data?.detail || 'Store sync failed', variant: 'destructive' });
    } finally {
      setSyncingStore(null);
      setConfirmDialog({ open: false, type: '', storeId: null });
    }
  };

  const getSyncStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Synced</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      case 'partial':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" /> Partial</Badge>;
      case 'never':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Never Synced</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getOverallStatusBadge = (status) => {
    switch (status) {
      case 'synced':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Fully Synced</Badge>;
      case 'partial':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" /> Partial</Badge>;
      case 'never_synced':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Never Synced</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sync-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Upload className="w-7 h-7 text-indigo-600" />
            Sync Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Sync products and pricing to all active stores</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setConfirmDialog({ open: true, type: 'products', storeId: null })}
            disabled={syncingAll}
            className="bg-indigo-600 hover:bg-indigo-700 gap-2"
          >
            {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            Sync All Products
          </Button>
          <Button
            onClick={() => setConfirmDialog({ open: true, type: 'pricing', storeId: null })}
            disabled={syncingAll}
            className="bg-purple-600 hover:bg-purple-700 gap-2"
          >
            {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />}
            Sync All Pricing
          </Button>
          <Button onClick={fetchSyncStatus} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Stores</p>
                <p className="text-2xl font-bold text-blue-800">{syncData.total_stores || 0}</p>
              </div>
              <Store className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Fully Synced</p>
                <p className="text-2xl font-bold text-emerald-800">{syncData.synced_stores || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Partial Sync</p>
                <p className="text-2xl font-bold text-amber-800">{syncData.partial_stores || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Never Synced</p>
                <p className="text-2xl font-bold text-red-800">{syncData.never_synced || 0}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Sync Status Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Store className="w-5 h-5" />
            Store Sync Status
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Store</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">HQ Products</TableHead>
                  <TableHead className="text-center">In Stock</TableHead>
                  <TableHead className="text-center">Product Sync</TableHead>
                  <TableHead className="text-center">Pricing Sync</TableHead>
                  <TableHead className="text-center">Overall Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-indigo-500" />
                      Loading sync status...
                    </TableCell>
                  </TableRow>
                ) : syncData.stores?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                      <Store className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No stores found
                    </TableCell>
                  </TableRow>
                ) : (
                  syncData.stores?.map((store) => (
                    <TableRow key={store.store_id} className="hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-800">{store.store_name}</p>
                          <p className="text-xs text-gray-500 font-mono">{store.store_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          store.store_type === 'franchise' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'
                        }>
                          {store.store_type === 'franchise' ? 'Franchise' : 'Owned'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{store.total_products}</TableCell>
                      <TableCell className="text-center">
                        <span className={store.products_in_stock > 0 ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                          {store.products_in_stock}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getSyncStatusBadge(store.last_product_sync?.status)}
                          <span className="text-xs text-gray-500">{formatDate(store.last_product_sync?.synced_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getSyncStatusBadge(store.last_pricing_sync?.status)}
                          <span className="text-xs text-gray-500">{formatDate(store.last_pricing_sync?.synced_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {getOverallStatusBadge(store.overall_status)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmDialog({ open: true, type: 'store_all', storeId: store.store_id })}
                            disabled={syncingStore === store.store_id}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          >
                            {syncingStore === store.store_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            <span className="ml-1">Sync</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Confirm Sync
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.type === 'products' && 'This will sync all products to all active stores.'}
              {confirmDialog.type === 'pricing' && 'This will sync all pricing information to all active stores.'}
              {confirmDialog.type === 'store_all' && 'This will sync products and pricing to this store.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: '', storeId: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog.type === 'products') handleBulkSync('products');
                else if (confirmDialog.type === 'pricing') handleBulkSync('pricing');
                else if (confirmDialog.type === 'store_all') handleStoreSync(confirmDialog.storeId, 'all');
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={syncingAll || syncingStore}
            >
              {(syncingAll || syncingStore) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default SyncDashboard;
